import React, {useEffect, useId, useState} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {useSubscription} from '@/shared/utils'
import {getLogger} from '@/shared/utils/logUtils'
import {useSharingAccess} from './hooks'
import {SHARED_FOLDER_ICON} from './constants'
import {shareErrorMessage} from './errors'
import {useSaveSharingSettings, useSharingSettings} from './managementHooks'
import type {InviteScope, SharingSettings} from './types'

/** Entries separated by commas, spaces or new lines; blanks and repeats dropped. */
const parseList = (text: string, lower: boolean): string[] => {
  const out: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const v = lower ? raw.trim().toLowerCase() : raw.trim()
    if (v && !out.includes(v)) out.push(v)
  }
  return out
}

/** Leading `@` is how people often write a domain; the setting wants the bare domain. */
const parseDomains = (text: string) => parseList(text.replace(/(^|[\s,;])@/g, '$1'), true)

/**
 * "Who can be invited" (D3, P1-A7): by default only members of this subscription; owners and
 * admins can allow anyone with an email address, optionally limited to some email domains and to
 * people from some subscriptions. Self-gates like the other Settings cards: invisible unless
 * sharing is on and the user is an owner or admin of the linked subscription.
 */
export const SharingSettingsSection: React.FC = () => {
  const {subscriptionId, role} = useSubscription()
  const {sharing} = useSharingAccess()
  const isManager = role === 'owner' || role === 'admin'
  const enabled = sharing && isManager && !!subscriptionId
  const settings = useSharingSettings(subscriptionId, enabled)

  if (!enabled || !subscriptionId) return null

  return (
    <div className='col-12 col-lg-6' data-testid='sharing-settings-section'>
      <div className='ob-page__section d-flex flex-column flex-grow-1'>
        <h3 className='ob-page__section-title'>
          <i className={`${SHARED_FOLDER_ICON} me-2`} aria-hidden='true' />
          Shared folders
        </h3>
        <p className='ob-text--muted ob-text--small ob-spacing--mb-4'>
          Members of this subscription can share folders from their My Files. Choose who they can
          invite.
        </p>
        <div className='card'>
          <div className='card-body'>
            {settings.isLoading ? (
              <div className='text-muted small'>Loading…</div>
            ) : settings.isError ? (
              <div className='alert alert-warning small mb-0' role='alert'>
                {shareErrorMessage(settings.error)}
              </div>
            ) : settings.data ? (
              <SharingSettingsForm subscriptionId={subscriptionId} saved={settings.data} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

const SharingSettingsForm: React.FC<{subscriptionId: string; saved: SharingSettings}> = ({
  subscriptionId,
  saved,
}) => {
  const [scope, setScope] = useState<InviteScope>(saved.inviteScope)
  const [domains, setDomains] = useState(saved.allowedDomains.join(', '))
  const [subscriptions, setSubscriptions] = useState(saved.allowedSubscriptions.join(', '))
  const [error, setError] = useState<string | null>(null)
  const save = useSaveSharingSettings(subscriptionId)
  const name = useId()
  const domainsId = useId()
  const subscriptionsId = useId()

  useEffect(() => {
    setScope(saved.inviteScope)
    setDomains(saved.allowedDomains.join(', '))
    setSubscriptions(saved.allowedSubscriptions.join(', '))
  }, [saved])

  const next: SharingSettings = {
    inviteScope: scope,
    allowedDomains: scope === 'anyone' ? parseDomains(domains) : [],
    allowedSubscriptions: scope === 'anyone' ? parseList(subscriptions, false) : [],
  }
  const dirty =
    next.inviteScope !== saved.inviteScope ||
    next.allowedDomains.join() !== saved.allowedDomains.join() ||
    next.allowedSubscriptions.join() !== saved.allowedSubscriptions.join()

  const submit = () => {
    setError(null)
    save.mutate(next, {
      onSuccess: () => toast.success('Sharing settings saved', toastDefaultConfig),
      onError: (err) => {
        setError(shareErrorMessage(err))
        getLogger().warn('Saving sharing settings failed', err)
      },
    })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <fieldset>
        <legend className='fs-6 fw-semibold mb-2'>Who can be invited</legend>
        <div className='form-check mb-2'>
          <input
            className='form-check-input'
            type='radio'
            name={name}
            id={`${name}-subscription`}
            checked={scope === 'subscription'}
            onChange={() => setScope('subscription')}
          />
          <label className='form-check-label' htmlFor={`${name}-subscription`}>
            Only members of this subscription
          </label>
        </div>
        <div className='form-check mb-3'>
          <input
            className='form-check-input'
            type='radio'
            name={name}
            id={`${name}-anyone`}
            checked={scope === 'anyone'}
            onChange={() => setScope('anyone')}
          />
          <label className='form-check-label' htmlFor={`${name}-anyone`}>
            Anyone with an email address
          </label>
        </div>
      </fieldset>

      {scope === 'anyone' && (
        <div className='ps-4'>
          <div className='mb-3'>
            <label className='form-label small fw-semibold' htmlFor={domainsId}>
              Only these email domains <span className='text-muted fw-normal'>(optional)</span>
            </label>
            <input
              id={domainsId}
              type='text'
              className='form-control form-control-sm'
              placeholder='lab.org, university.edu'
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
            />
            <div className='form-text'>Leave empty to allow any domain. Checked when inviting.</div>
          </div>
          <div className='mb-3'>
            <label className='form-label small fw-semibold' htmlFor={subscriptionsId}>
              Only people from these subscriptions{' '}
              <span className='text-muted fw-normal'>(optional)</span>
            </label>
            <input
              id={subscriptionsId}
              type='text'
              className='form-control form-control-sm'
              placeholder='Subscription keys, separated by commas'
              value={subscriptions}
              onChange={(e) => setSubscriptions(e.target.value)}
            />
            <div className='form-text'>
              Leave empty to allow anyone. Checked when the invitation is accepted.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className='alert alert-danger small' role='alert'>
          {error}
        </div>
      )}
      <div className='d-flex justify-content-end'>
        <button
          type='submit'
          className='btn btn-sm btn-primary'
          disabled={!dirty || save.isLoading}
        >
          {save.isLoading ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}
